import React, { useCallback, useMemo, useState } from "react";
import iconReturn from '../../../../assets/img/icon/return.svg';
import iconSave from '../../../../assets/img/icon/save.svg';
import { Env } from "../../../../models/env";
import { UuidGenerator } from "../../../../utilities/uuid-generator";
import { Button } from "../../../ui/button/button";
import { Header } from "../../../ui/header/header";
import {PrintUploadImageList} from './component/print-upload-image-list';
import '../../../ui/file-drop-zone/file-drop-zone.scss';
import './print-image-upload.scss';
import { usePrintImageUpload } from "./hooks/use-print-image-upload";
import { ProgressBar } from '../../../ui/progress-bar/progress-bar';
import { useAppDispatch } from '../../../../app/hooks';
import { push } from "connected-react-router";
import { PathParams, RoutingPath } from "../../../../routes/routing-path";
import { useParams } from "react-router-dom";
import { useApiActions } from "../../../../hooks/use-api-actions";
import { apiActions } from "../../../../slices/api-slice";
import { ApiImagesPost } from '../../../../api/front/images/api-images';
import { ImageProcessorManager, IMAGES_KIND } from "../../../../layout-editor/manager/image-processor/image-processor.manager";
import { ApiPrintImagePost } from "../../../../api/sift/front/api-print-image";
import { TrimUiManager } from "../../../../trim-editor/manager/ui/trim.ui.manager";
import { usePrintImages } from "../hooks/use-print-images";
import { usePrintStatus } from "../hooks/use-print-status";
import { usePrintUploadFiles } from "../hooks/use-print-upload-files";

export const PrintImageUpload = () => {
  const { filterUploadFiles } = usePrintUploadFiles();
  const {
    imageUpload,
    uploadProgressView: progressView,
    uploadProgress: progress,
  } = usePrintImages();
  usePrintStatus({ isLockedAfterOrder: true });
  const dispatch = useAppDispatch();
  const { kijshopCd, shopOrderId } = useParams<PathParams>();

  const id = useMemo(() => UuidGenerator.create(), [])
  const [focus, setFocus] = useState(false);
  const [uploadImageList, setUploadImageList] = useState<{ src: string, name: string, size: number }[]>([]);

  const { asyncApiRun } = useApiActions();

  const onFileUpload = useCallback(async (uploadFiles: File[]) => {
    const sequential = await filterUploadFiles(uploadFiles);
    if (!sequential.length) {
      return;
    }
    const format = (file: File) => new Promise<{ src: string, name: string, size: number }>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({ name: file.name, src: reader.result as string, size: file.size });
      }
      reader.readAsDataURL(file);
    });
    (async() => {
      for (const imgData of sequential) {
        const img = await format(imgData);
        setUploadImageList((prev) => [...prev, img]);
      }
    })();
    imageUpload(sequential);
  }, [
    filterUploadFiles,
    imageUpload,
  ]);

  const handleChangeFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadFiles = Array.from(e.target.files ?? []);
    onFileUpload(uploadFiles);
  }, [
    onFileUpload,
  ]);

  const handleDropFile = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setFocus(false);
    const uploadFiles = Array.from(e.dataTransfer?.files ?? []);
    onFileUpload(uploadFiles);
  }, [
    onFileUpload,
  ]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setFocus(true);
  }, [
  ]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setFocus(false);
  }, []);

  const handleClickNext = useCallback(() => {
    dispatch(push(RoutingPath.cnv.printOrderAddOrderSelect({ kijshopCd, shopOrderId })))
  }, [
    kijshopCd,
    shopOrderId,
    dispatch,
  ]);

  const returnTop = useCallback(() => {
    dispatch(push(RoutingPath.cnv.printOrder({ kijshopCd, shopOrderId })));
  }, [shopOrderId, dispatch, kijshopCd]);

  return (
    <div className="column_direction_wrap">
      <Header />
      <div className="print_image_upload">
        <div className="print_image_upload__menu">
          <div>
            <Button
              label="商品一覧へ戻る"
              icon={<img
                src={iconReturn}
                alt=""
              />}
              onClick={returnTop}
              color="light"
            />
            <Button
              label="商品選択へ進む"
              color="warning"
              onClick={handleClickNext}
            />
          </div>
        </div>
      </div>
      <div className="print_image_upload__content">
        <div className="bottom_border_header search_print_order__header">
          <div>
            写真アップロード
          </div>
          {!!progressView && (
            <div className="upload_progress">
              <ProgressBar
                percent={Number(progressView)}
                progressColor="#4B73E7"
                edgeColor="#666"
                label={
                  `${Number(progressView)}％ ( ${((progress?.done ?? 0) + (progress?.error ?? 0))} / ${((progress?.done ?? 0) + (progress?.error ?? 0) + (progress?.queue ?? 0))} )`
                }
              />
            </div>
          )}
        </div>
        <div className="file_drop_zone">
          <div className="file_drop_zone__drop__wrap">
            <div
              className={`file_drop_zone__drop ${focus ? 'drag_accept' : ''}`}
              onDrop={handleDropFile}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <span>ここに注文する画像をドラッグ＆ドロップしてください。</span>
              <div className={'file_drop_zone__drop__buttons'}>
                <label
                  htmlFor={`file-upload-input-${id}`}
                  className="button sm warning"
                >
                  画像を選択
                  <input
                    id={`file-upload-input-${id}`}
                    type="file"
                    accept="image/jpg, image/jpeg"
                    multiple
                    onChange={handleChangeFile}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
        {!!uploadImageList.length && < PrintUploadImageList list={uploadImageList} />}
      </div>
    </div>
  )
}